import { globalTrialLengthDays } from 'globalConstants';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { addDateDays, getDateTimeDiffHours } from 'utils/datetime';
import { SubscriptionTypes } from 'utils/subscription';
import { IdentityProviderType } from 'models/identity/identityProvider';
import Tenant from 'models/tenants/tenant';

/**
 * Why a suspended organization is closed. Mirrored by numeric value from the API (LoginTenantRestoreStates,
 * Core/Data/DTOs/User/LoginDTO.cs); None for an organization that is not suspended.
 */
export enum LoginTenantRestoreStates {
  None = 0,
  //a backup restore is queued or running
  InProgress = 1,
  //a failed overwrite restore holds the organization until a restore completes
  Failed = 2,
  //suspended for any other reason
  Suspended = 3,
}

//
// The answer of GET users/login, the start of every session: who the API sees, whether the organization
// and the user are licensed, the roles, the organizations the user can switch to, and the reference data
// the app caches for the session (globalDataCache).
//
export default class Login {
  tenantId: string; //the tenant the back-end resolved; empty while the user's directory has no organization

  userId?: string;

  //the identity provider the user signed in with, and the one the resolved tenant is registered with
  identityProviderType: IdentityProviderType;

  tenantIdentityProviderType: IdentityProviderType;

  lastLogin?: Date;

  tenantLicensed: boolean;

  userLicensed: boolean;

  userSystemRoles?: number[];

  userLanguageCode?: string;

  defLanguageCode: string;

  trialStart?: Date;

  subscriptionType: SubscriptionTypes;

  tenants: Tenant[];

  isOnboardingInProgress: boolean;

  //the organization may publish marketplace content packs (phase 14); gates the Publishing tab
  isPublisher: boolean;

  //not None only for a suspended organization: tenantLicensed and userLicensed are false then, and userSystemRoles
  //is filled in InProgress and Failed only
  tenantRestoreState: LoginTenantRestoreStates;

  globalDataCache: GlobalDataCache;

  //set by the client: the session is in an organizational unit, not in the top level organization
  isOrgUnit: boolean;

  constructor() {
    this.tenantId = '';
    this.identityProviderType = IdentityProviderType.Entra;
    this.tenantIdentityProviderType = IdentityProviderType.Entra;
    this.tenantLicensed = false;
    this.userLicensed = false;
    this.defLanguageCode = '';
    this.subscriptionType = SubscriptionTypes.Trial;
    this.tenants = [];
    this.isOnboardingInProgress = false;
    this.isPublisher = false;
    this.tenantRestoreState = LoginTenantRestoreStates.None;
    this.globalDataCache = new GlobalDataCache();
    this.isOrgUnit = false;
  }

  /** A backup restore holds the organization: the API opens only the reads of Administration > Backup. */
  isHeldByRestore(): boolean {
    return (
      this.tenantRestoreState === LoginTenantRestoreStates.InProgress ||
      this.tenantRestoreState === LoginTenantRestoreStates.Failed
    );
  }

  isInTrial(): boolean {
    return this.trialStart !== undefined;
  }

  trialEnd(): Date | undefined {
    if (!this.trialStart) return undefined;

    return addDateDays(this.trialStart, globalTrialLengthDays);
  }

  trialDaysLeft(): number {
    const trialEnd = this.trialEnd();
    if (!trialEnd) return -1;
    const trialOverDueHours = getDateTimeDiffHours(trialEnd, new Date());
    if (trialOverDueHours < 0) return 0;

    return Math.ceil(trialOverDueHours / 24);
  }
}
